import type { ComponentProps, FC, PropsWithChildren, ReactNode } from 'react';
import { Box } from 'reflexbox';

import { Avatar } from './Avatar';
import { FlexGap } from './FlexGap';
import { Paragraph } from './Text';

type User = NonNullable<ComponentProps<typeof Avatar>['user']>;

type Props<U extends User = User> = PropsWithChildren<{
	user: U;
	renderUserTitle?: (user: U) => ReactNode;
	supertitle?: string;
	subtitle?: string;
	avatarProps?: Omit<ComponentProps<typeof Avatar>, 'user'>;
	contrast?: 'default' | 'low';
	preventContentGrow?: boolean;
	/** Keep name and subtitle on one line each so a narrow cell can ellipsize them. */
	truncate?: boolean;
}> &
	Omit<ComponentProps<typeof FlexGap>, 'children' | 'gap' | 'alignItems'>;

export const UserRow: FC<Props> = ({
	user,
	contrast = 'default',
	preventContentGrow,
	truncate,
	avatarProps = {},
	renderUserTitle,
	supertitle,
	subtitle,
	children,
	...rest
}) => {
	return (
		<FlexGap
			gap={8}
			alignItems="center"
			{...rest}
			minWidth={truncate ? 0 : rest.minWidth}
			width={truncate ? '100%' : rest.width}
		>
			<Avatar user={user} size={32} {...avatarProps} flexShrink={0} />
			<Box
				flexGrow={preventContentGrow ? 0 : 1}
				flexShrink={truncate ? 1 : 0}
				minWidth={truncate ? 0 : undefined}
				style={truncate ? { overflow: 'hidden' } : undefined}
			>
				{supertitle && (
					<Paragraph small mb="-2px" lineClamp={truncate ? 1 : undefined}>
						{supertitle}
					</Paragraph>
				)}
				<Paragraph
					color={contrast === 'default' ? 'antracite' : undefined}
					lineClamp={truncate ? 1 : undefined}
				>
					{renderUserTitle ? renderUserTitle(user) : `${user.firstName} ${user.lastName}`}
				</Paragraph>
				{subtitle && (
					<Paragraph small mt="-2px" lineClamp={truncate ? 1 : undefined}>
						{subtitle}
					</Paragraph>
				)}
			</Box>
			{children}
		</FlexGap>
	);
};
